/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import {
  API_RESPONSE_CODE,
  type AlertBanner
} from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../Graphql/utils";
import { listCompanyStoresQuery } from "../../Graphql/querries/companyStoresQuery";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

export interface CompanyStoreOption {
  id: string;
  name: string;
}

interface ListStoresGraphqlPayload {
  listStores: {
    code: string;
    stores?: { id: string; name?: string }[];
  };
}

export interface UseCompanyStoresParams {
  /** When false, no fetch runs (e.g. modal closed or not company admin). */
  enabled: boolean;
  companyId: string | undefined;
  setAlert: (alert: AlertBanner) => void;
}

/**
 * Loads company stores via `listStores` GraphQL for company-admin flows (modals, pickers).
 * Aborts updates if `enabled` / `companyId` change or the component unmounts.
 */
export function useCompanyStores({
  enabled,
  companyId,
  setAlert
}: UseCompanyStoresParams): {
  stores: CompanyStoreOption[];
  loading: boolean;
} {
  const [stores, setStores] = useState<CompanyStoreOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !companyId?.trim()) {
      return;
    }

    const cid = companyId.trim();
    let cancelled = false;

    (async () => {
      setLoading(true);

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching company stores",
        buildLogContext(
          "company-stores",
          "get-company-stores",
          undefined, //Company stores are fetched at company level, not user-specific
          cid
        )
      );

      try {
        const res = await fetchGraphQL<ListStoresGraphqlPayload, any>(
          listCompanyStoresQuery,
          { input: { companyId: cid } },
          setAlert
        );

        if (cancelled) return;

        const block = res?.listStores;

        // ⚠️ Expected API/business failure
        if (!block || block.code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch company stores",
            buildLogContext(
              "company-stores",
              "get-company-stores",
              undefined, // Company stores are fetched at company level, not user-specific
              cid,
              undefined, // Company stores are not store-scoped
              {
                apiCode: block ? String(block.code) : "NO_RESPONSE"
              }
            )
          );
          setStores([]);
          return;
        }

        const mapped: CompanyStoreOption[] = (block.stores ?? []).map((s) => ({
          id: s.id,
          name: s.name?.trim() ? s.name : s.id
        }));

        setStores(mapped);

        sendAppLog(
          LOG_LEVEL.INFO,
          "Company stores fetched successfully",
          buildLogContext(
            "company-stores",
            "get-company-stores",
            undefined, // Company stores are fetched at company level, not user-specific
            cid,
            undefined, // Company stores are not store-scoped
            {
              storeCount: mapped.length
            }
          )
        );
      } catch (error) {
        // ❌ Unexpected runtime/network error
        if (!cancelled) {
          captureAppException(
            "Failed to fetch company stores",
            error,
            buildLogContext(
              "company-stores",
              "get-company-stores",
              undefined, // Company stores are fetched at company level, not user-specific
              cid
            )
          );
          setStores([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, companyId, setAlert]);

  return { stores, loading };
}
